import { fixedUi } from '@/i18n/fixedUi';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import SaveSizeChartTemplateMetaModal from '@/temu-platform/modules/templates/SaveSizeChartTemplateMetaModal';
import {
  applyOfficialSizeTemplate,
  asSizeChartList,
  buildSizeChartRecords,
  extractSizeNameFromRecordValues,
  isOfficialSizeChartTemplatePayload,
  officialSizeChartDraftHasContent,
  officialSizeChartRecordsHaveSizeNames,
  officialSizeChartRecordsHaveValues,
  parseSizeChartClass,
  pickSizeMetaLists,
  resolveOfficialSizeTemplateIds,
  validateOfficialSizeChartDraft,
} from '@/temu-platform/utils/temuOfficialSizeChart';
import {
  buildShoeSizeChartRecords,
  getStaticShoeFootMap,
  isTemuShoeSizeClass,
  listShoeFootRows,
} from '@/temu-platform/utils/temuShoeSizeFootMap';
import { TEMU_TEMPLATE_SCOPES } from '@/temu-platform/constants/temuTemplateScope';
import { pickAutoRuleTemplate } from '@/temu-platform/utils/temuTemplateAutoRule';
import { adminAPI } from '@/utils/adminApi';
import { temuPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

const DEFAULT_CHART_NAME = '发品尺码表';
const LIB_IPE = 'ipe';
const LIB_OFFICIAL = 'official';

function librarySelectValue(source, id) {
  const sid = String(id || '').trim();
  if (!sid) return '';
  return source === LIB_OFFICIAL ? `official:${sid}` : `ipe:${sid}`;
}

function parseLibrarySelectValue(raw) {
  const value = String(raw || '').trim();
  if (value.startsWith('official:')) {
    return { source: LIB_OFFICIAL, id: value.slice('official:'.length) };
  }
  if (value.startsWith('ipe:')) {
    return { source: LIB_IPE, id: value.slice(4) };
  }
  return { source: '', id: '' };
}

function tagLibraryItems(items, source) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => String(item?.id || '').trim())
    .map((item) => ({ ...item, _lib: source }));
}

function extractOfficialSizeRecords(payload) {
  const raw = payload && typeof payload === 'object' ? payload : {};
  const draft = raw.officialSizeChart && typeof raw.officialSizeChart === 'object'
    ? raw.officialSizeChart
    : null;
  return Array.isArray(draft?.records) ? draft.records : [];
}

function uniqueLibraryItems(items) {
  const seen = new Set();
  const out = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    const key = `${item?._lib || ''}:${String(item?.id || '').trim()}`;
    if (!item?.id || seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });
  return out;
}

function categoryQueryKeys(productTypeId, categoryId) {
  return [...new Set(
    [productTypeId, categoryId].map((v) => String(v || '').trim()).filter(Boolean),
  )];
}

async function fetchSizeChartLibraryList({ officialMode, shopId, shopType, productTypeId, categoryId }) {
  const keys = categoryQueryKeys(productTypeId, categoryId);
  if (!keys.length) return [];
  if (officialMode) {
    const chunks = await Promise.all(keys.map((id) => (
      adminAPI.listOfficialTemuSizeChartTemplates({
        productTypeId: id,
        shopType,
        page: 1,
        pageSize: 100,
      }).then((res) => tagLibraryItems(res?.items, LIB_OFFICIAL)).catch(() => [])
    )));
    return uniqueLibraryItems(chunks.flat());
  }
  if (!shopId) return [];
  const chunks = await Promise.all(keys.flatMap((id) => ([
    temuPlatformAPI.listSizeChartTemplates(shopId, {
      productTypeId: id,
      page: 1,
      pageSize: 100,
    }).then((res) => tagLibraryItems(res?.items, LIB_IPE)).catch(() => []),
    temuPlatformAPI.listSizeChartTemplates(shopId, {
      productTypeId: id,
      page: 1,
      pageSize: 100,
      scope: TEMU_TEMPLATE_SCOPES.official,
    }).then((res) => tagLibraryItems(res?.items, LIB_OFFICIAL)).catch(() => []),
  ])));
  return uniqueLibraryItems(chunks.flat());
}

function fetchSizeChartLibraryDetail({ officialMode, shopId, source, id }) {
  if (officialMode || source === LIB_OFFICIAL) {
    if (officialMode) return adminAPI.getOfficialTemuSizeChartTemplate(id);
    return temuPlatformAPI.getSizeChartTemplate(shopId, id, { scope: TEMU_TEMPLATE_SCOPES.official });
  }
  return temuPlatformAPI.getSizeChartTemplate(shopId, id);
}

/** 落库 records 有测量值但缺 elementList 时，从 values key 推断列（排除尺码分组 id） */
function inferElementsFromRecords(records, groupList = []) {
  const groupId = String(groupList[0]?.id ?? 1);
  const skip = new Set([groupId, '1', 'sizeName', 'groupId', 'name']);
  const seen = new Map();
  (Array.isArray(records) ? records : []).forEach((row) => {
    const values = row?.values && typeof row.values === 'object' ? row.values : {};
    Object.keys(values).forEach((key) => {
      const k = String(key);
      if (skip.has(k) || seen.has(k)) return;
      if (values[k] == null || String(values[k]).trim() === '') return;
      seen.set(k, { id: Number.isFinite(Number(k)) ? Number(k) : k, name: `属性${k}`, necessary: true });
    });
  });
  return Array.from(seen.values());
}

/**
 * Temu 官方尺码表面板（class.get / meta.get / settings.get / create）。
 * 默认建立尺码表；行数跟随主规格已选尺码；可从站内尺码库 / 平台尺码表库带出测量值。
 *
 * @param {boolean} [hideLibraryActions] 尺码库新建/编辑场景：隐藏「从库选用 / 保存为模板」（本页即在建库）
 * @param {string} [emptySizeHint] 未选尺码时的提示文案
 */
const TemuOfficialSizeChartPanel = forwardRef(function TemuOfficialSizeChartPanel({
  shopId = '',
  /** Admin 官方模板：无 shopId，按 shopType 借已授权店拉平台规则 */
  officialMode = false,
  shopType = '',
  categoryId = '',
  productTypeId = '',
  categoryPathLabel = '',
  selectedSizeIds = [],
  /** 主规格已选尺码名称，决定表格行 */
  selectedSizeLabels = [],
  /** 保存弹窗预填款号 */
  defaultStyleNo = '',
  required = false,
  sizeClass = null,
  loading = false,
  errorMessage = '',
  /** 产品模板 / 草稿回填 */
  initialDraft = null,
  onDraftChange,
  hideLibraryActions = false,
  emptySizeHint = fixedUi('请先在规格供应中选择尺码，尺寸表将按所选尺码自动增减行。'),
}, ref) {
  useTranslation('workbench');
  const [busy, setBusy] = useState(false);
  /** create：编辑建表（默认）；template：套装等多表仅选库提交 */
  const [mode, setMode] = useState('create');
  const [metaRes, setMetaRes] = useState(null);
  const [settingsRes, setSettingsRes] = useState(null);
  /** 平台尺码表库（发品引用 businessId） */
  const [platformTemplates, setPlatformTemplates] = useState([]);
  /** 站内 IPE 尺码库（含款号 / 自动规则） */
  const [ipeTemplates, setIpeTemplates] = useState([]);
  const [selectedLibraryId, setSelectedLibraryId] = useState('');
  const [selectedBusinessIds, setSelectedBusinessIds] = useState([]);
  const [records, setRecords] = useState([]);
  const recordsRef = useRef(records);
  recordsRef.current = records;
  const [loadError, setLoadError] = useState('');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const lastEmittedDraftKeyRef = useRef('');
  const [isDoubleSize, setIsDoubleSize] = useState(() => Boolean(initialDraft?.isDoubleSize));
  const [shoeFootMap, setShoeFootMap] = useState(() => getStaticShoeFootMap());
  const selectedSizeLabelsRef = useRef(selectedSizeLabels);
  selectedSizeLabelsRef.current = selectedSizeLabels;
  const initialDraftRef = useRef(initialDraft);
  initialDraftRef.current = initialDraft;
  const draftSeededKeyRef = useRef('');

  const { groupList, elementList } = useMemo(
    () => pickSizeMetaLists(metaRes, settingsRes),
    [metaRes, settingsRes],
  );
  const groupId = String(groupList[0]?.id ?? 1);
  const needCount = Number(sizeClass?.classType) === 1 ? 2 : 1;
  const suitMulti = needCount > 1;
  const shoeClass = isTemuShoeSizeClass(sizeClass?.classId);
  const shoeOptsRef = useRef({
    shoeClass: false,
    classId: null,
    isDoubleSize: false,
    footMap: getStaticShoeFootMap(),
  });
  shoeOptsRef.current = {
    shoeClass,
    classId: sizeClass?.classId,
    isDoubleSize,
    footMap: shoeFootMap,
  };

  const normalizedSizeLabels = useMemo(() => {
    const fromSelection = (selectedSizeLabels || [])
      .map((n) => String(n || '').trim())
      .filter(Boolean);
    if (fromSelection.length) return fromSelection;
    // 复制/回填的产品模板可能只带回尺码表记录而未带回主规格选择：
    // 从已加载记录兜底推导尺码名，避免「保存为模板」按钮一直置灰
    const fromRecords = [];
    const seen = new Set();
    (Array.isArray(records) ? records : []).forEach((row) => {
      const name = extractSizeNameFromRecordValues(row?.values, groupList);
      const key = String(name || '').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      fromRecords.push(key);
    });
    return fromRecords;
  }, [selectedSizeLabels, records, groupList]);

  const syncRowsToSizeLabels = useCallback((labels, groups, elements, prevRecords = []) => {
    const opts = shoeOptsRef.current || {};
    const base = buildSizeChartRecords({
      groupList: groups,
      elementList: elements,
      sizeNames: labels,
      prevRecords,
    });
    if (!opts.shoeClass) return base;
    const footRows = listShoeFootRows(opts.footMap, opts.classId, opts.isDoubleSize);
    const filled = buildShoeSizeChartRecords({
      sizeLabels: labels,
      footRows,
      groupList: groups,
      elementList: elements,
    });
    return base.map((row, i) => {
      const auto = filled[i]?.values || {};
      const values = { ...(row.values || {}) };
      Object.keys(auto).forEach((k) => {
        if (!String(values[k] || '').trim() && String(auto[k] || '').trim()) {
          values[k] = auto[k];
        }
      });
      return { values };
    });
  }, []);

  const libraryTypeId = String(productTypeId || categoryId || '').trim();
  const autoAppliedKeyRef = useRef('');

  const reloadIpeTemplates = useCallback(async () => {
    if (hideLibraryActions) {
      setIpeTemplates([]);
      return;
    }
    if (!String(productTypeId || categoryId || '').trim() || (officialMode ? !shopType : !shopId)) {
      setIpeTemplates([]);
      return;
    }
    try {
      const items = await fetchSizeChartLibraryList({
        officialMode,
        shopId,
        shopType,
        productTypeId,
        categoryId,
      });
      setIpeTemplates(items);
    } catch {
      setIpeTemplates([]);
    }
  }, [hideLibraryActions, officialMode, shopId, shopType, productTypeId, categoryId]);

    const loadContext = useCallback(async () => {
    const draft = initialDraftRef.current;
    const draftGroups = Array.isArray(draft?.groupList) ? draft.groupList : [];
    const draftElements = Array.isArray(draft?.elementList) ? draft.elementList : [];
    const draftRecords = Array.isArray(draft?.records) ? draft.records : [];
    // 尺码库编辑：payload 已落库 records 时直接展示；缺 meta 时用默认尺码列，不再卡「加载尺码规则」
    const hasLocalDraft = hideLibraryActions && draftRecords.length > 0;
    if (hasLocalDraft) {
      const groups = draftGroups.length
        ? draftGroups
        : [{ id: 1, name: '尺码' }];
      const elements = draftElements.length
        ? draftElements
        : inferElementsFromRecords(draftRecords, groups);
      setBusy(false);
      setLoadError('');
      setMode('create');
      setMetaRes({ sizeSpecMeta: { groupList: groups, elementList: elements } });
      setSettingsRes(null);
      setPlatformTemplates([]);
      setSelectedLibraryId(String(draft.selectedLibraryId || '').trim());
      setSelectedBusinessIds(
        (Array.isArray(draft.selectedBusinessIds) ? draft.selectedBusinessIds : [])
          .map((id) => Number(id))
          .filter((id) => id > 0),
      );
      const labels = (selectedSizeLabelsRef.current || [])
        .map((n) => String(n || '').trim())
        .filter(Boolean);
      const fallbackLabels = labels.length
        ? labels
        : draftRecords.map((r) => extractSizeNameFromRecordValues(
          r?.values && typeof r.values === 'object' ? r.values : {},
          groups,
        )).filter(Boolean);
      // 尺码名尚未就绪时保留落库 records，避免先 emit 空表把父级草稿冲掉
      if (!fallbackLabels.length) {
        setRecords(draftRecords);
      } else {
        setRecords(syncRowsToSizeLabels(fallbackLabels, groups, elements, draftRecords));
      }
      draftSeededKeyRef.current = `draft-local|${draft.selectedLibraryId || draftRecords.length}`;
      return;
    }

    const needRules = required || isTemuShoeSizeClass(sizeClass?.classId);
    const canCallPlatform = officialMode
      ? Boolean(shopType && categoryId && needRules && sizeClass?.classId)
      : Boolean(shopId && categoryId && needRules && sizeClass?.classId);
    if (!canCallPlatform) {
      setMode('skip');
      setMetaRes(null);
      setSettingsRes(null);
      setPlatformTemplates([]);
      setSelectedLibraryId('');
      setSelectedBusinessIds([]);
      setRecords([]);
      draftSeededKeyRef.current = '';
      return;
    }
    setBusy(true);
    setLoadError('');
    try {
      const classId = Number(sizeClass.classId);
      const catId = Number(categoryId);
      const seedKey = officialMode
        ? `official|${shopType}|${catId}|${classId}`
        : `${shopId}|${catId}|${classId}`;
      const catalogBody = {
        shopType,
        catId,
        categoryId: catId,
        classId,
      };
      const [meta, settings, templatesRes] = await Promise.all([
        officialMode
          ? adminAPI.getOfficialTemuCatalogSizeChartMeta(catalogBody)
          : temuPlatformAPI.getPublishSizeChartMeta(shopId, catId, classId),
        officialMode
          ? adminAPI.getOfficialTemuCatalogSizeChartSettings(catalogBody)
          : temuPlatformAPI.getPublishSizeChartSettings(shopId, catId, classId),
        (hideLibraryActions || officialMode)
          ? Promise.resolve(null)
          : temuPlatformAPI.listPublishSizeChartTemplates(shopId, {
            catId,
            offset: 0,
            pageSize: 50,
          }),
      ]);
      setMetaRes(meta);
      setSettingsRes(settings);
      const list = (hideLibraryActions || officialMode) ? [] : asSizeChartList(templatesRes);
      setPlatformTemplates(list);
      if (!hideLibraryActions) {
        void reloadIpeTemplates();
      }
      const { groupList: groups, elementList: elements } = pickSizeMetaLists(meta, settings);
      const labels = (selectedSizeLabelsRef.current || [])
        .map((n) => String(n || '').trim())
        .filter(Boolean);
      const draftHasContent = officialSizeChartDraftHasContent(draft, groups);
      const canSeed = draftHasContent && draftSeededKeyRef.current !== seedKey;
      if (canSeed) {
        draftSeededKeyRef.current = seedKey;
        if (hideLibraryActions) {
          setMode('create');
        } else if (draft.mode === 'template' || draft.mode === 'create' || draft.mode === 'skip') {
          setMode(draft.mode);
        } else {
          setMode(Number(sizeClass?.classType) === 1 ? 'template' : 'create');
        }
        const seededRecords = Array.isArray(draft.records) ? draft.records : [];
        setRecords(syncRowsToSizeLabels(labels, groups, elements, seededRecords));
        setSelectedBusinessIds(
          hideLibraryActions || officialMode
            ? []
            : (Array.isArray(draft.selectedBusinessIds) ? draft.selectedBusinessIds : [])
              .map((id) => Number(id))
              .filter((id) => id > 0),
        );
        setSelectedLibraryId(hideLibraryActions ? '' : String(draft.selectedLibraryId || '').trim());
      } else if (draftSeededKeyRef.current !== seedKey) {
        setRecords(syncRowsToSizeLabels(labels, groups, elements, []));
        setMode(hideLibraryActions
          ? 'create'
          : (Number(sizeClass?.classType) === 1 ? 'template' : 'create'));
        setSelectedLibraryId('');
        setSelectedBusinessIds([]);
        if (draftHasContent) {
          draftSeededKeyRef.current = seedKey;
        }
      }
    } catch (e) {
      setLoadError(e?.message || chartText('sizeChartPanel.rulesFail', '尺码表规则加载失败'));
      setMode('create');
    } finally {
      setBusy(false);
    }
  }, [
    officialMode,
    shopType,
    shopId,
    categoryId,
    required,
    sizeClass,
    hideLibraryActions,
    reloadIpeTemplates,
    syncRowsToSizeLabels,
  ]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  useEffect(() => {
    initialDraftRef.current = initialDraft;
    if (busy || loading || !required || !sizeClass?.classId) return;
    const draft = initialDraft;
    if (!officialSizeChartDraftHasContent(draft, groupList)) return;
    if (officialSizeChartRecordsHaveValues(recordsRef.current, groupList)) return;
    const seedKey = officialMode
      ? `official|${shopType}|${Number(categoryId)}|${Number(sizeClass.classId)}`
      : `${shopId}|${Number(categoryId)}|${Number(sizeClass.classId)}`;
    // 同一尺码类目已经灌过草稿后，用户清空测量值要留空，不能再把保存值填回来
    if (
      draftSeededKeyRef.current === seedKey
      && officialSizeChartRecordsHaveSizeNames(recordsRef.current, groupList)
    ) {
      return;
    }
    draftSeededKeyRef.current = seedKey;
    if (draft.mode === 'template' || draft.mode === 'create') {
      setMode(draft.mode);
    }
    setRecords(syncRowsToSizeLabels(
      normalizedSizeLabels,
      groupList,
      elementList,
      Array.isArray(draft.records) ? draft.records : [],
    ));
    if (Array.isArray(draft.selectedBusinessIds)) {
      setSelectedBusinessIds(
        draft.selectedBusinessIds.map((id) => Number(id)).filter((id) => id > 0),
      );
    }
    if (draft.selectedLibraryId != null) {
      setSelectedLibraryId(String(draft.selectedLibraryId || '').trim());
    }
  }, [
    initialDraft,
    busy,
    loading,
    required,
    sizeClass?.classId,
    shopId,
    categoryId,
    officialMode,
    shopType,
    groupList,
    elementList,
    normalizedSizeLabels,
    syncRowsToSizeLabels,
  ]);

  useEffect(() => {
    if (busy || loading || mode === 'skip') return;
    setRecords((prev) => {
      const labels = (normalizedSizeLabels || []).map((n) => String(n || '').trim()).filter(Boolean);
      // 上方尺码标签尚未解析出名称时，不要用空 names 把已填测量值洗成空表
      if (!labels.length) {
        if (officialSizeChartRecordsHaveValues(prev, groupList)) return prev;
        if (officialSizeChartRecordsHaveSizeNames(prev, groupList)) return prev;
        const draft = initialDraftRef.current;
        if (Array.isArray(draft?.records) && draft.records.length) return draft.records;
        return prev;
      }
      const keepEditingRows = officialSizeChartRecordsHaveValues(prev, groupList)
        || officialSizeChartRecordsHaveSizeNames(prev, groupList);
      const source = keepEditingRows
        ? prev
        : (officialSizeChartDraftHasContent(initialDraftRef.current, groupList)
          ? (initialDraftRef.current.records || [])
          : prev);
      const next = syncRowsToSizeLabels(labels, groupList, elementList, source);
      // 内容未变时复用 prev，避免 records 新引用 → onDraftChange → 父级 setState 套娃
      if (
        prev.length === next.length
        && prev.every((row, i) => {
          const a = row?.values || {};
          const b = next[i]?.values || {};
          const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
          for (const k of keys) {
            if (String(a[k] ?? '') !== String(b[k] ?? '')) return false;
          }
          return true;
        })
      ) {
        return prev;
      }
      return next;
    });
  }, [normalizedSizeLabels, groupList, elementList, busy, loading, mode, syncRowsToSizeLabels]);

  useEffect(() => {
    const emit = onDraftChangeRef.current;
    if (typeof emit !== 'function') return;
    if (busy || loading || mode === 'skip') return;
    // 编辑回填中：若本地 records 尚空而 initialDraft 有值，先别把空壳写回父级
    if (
      hideLibraryActions
      && !officialSizeChartRecordsHaveSizeNames(records, groupList)
      && !officialSizeChartRecordsHaveValues(records, groupList)
      && officialSizeChartDraftHasContent(initialDraftRef.current, groupList)
    ) {
      return;
    }
    const draft = {
      mode,
      sizeClass,
      selectedBusinessIds,
      selectedLibraryId,
      records,
      groupList,
      elementList,
      isDoubleSize: shoeClass ? Boolean(isDoubleSize) : false,
    };
    // 内容未变勿回写：父级 onDraftChange 常非 useCallback，依赖它会「父 setState → 新函数 → 再 emit」死循环
    let draftKey = '';
    try {
      draftKey = JSON.stringify({
        mode: draft.mode,
        classId: draft.sizeClass?.classId ?? null,
        selectedBusinessIds: draft.selectedBusinessIds,
        selectedLibraryId: draft.selectedLibraryId,
        isDoubleSize: draft.isDoubleSize,
        records: (draft.records || []).map((row) => row?.values || {}),
      });
    } catch {
      draftKey = `${draft.mode}|${draft.sizeClass?.classId || ''}|${draft.selectedLibraryId || ''}|${(draft.records || []).length}`;
    }
    if (draftKey === lastEmittedDraftKeyRef.current) return;
    lastEmittedDraftKeyRef.current = draftKey;
    emit(draft);
  }, [
    busy,
    loading,
    mode,
    sizeClass,
    selectedBusinessIds,
    selectedLibraryId,
    records,
    groupList,
    elementList,
    hideLibraryActions,
    shoeClass,
    isDoubleSize,
  ]);

  useImperativeHandle(ref, () => ({
    validate() {
      return validateOfficialSizeChartDraft({
        required,
        mode,
        sizeClass,
        selectedBusinessIds,
        records,
        groupList,
        elementList,
      });
    },
    async resolveForSubmit() {
      const check = validateOfficialSizeChartDraft({
        required,
        mode,
        sizeClass,
        selectedBusinessIds,
        records,
        groupList,
        elementList,
      });
      if (!check.ok) throw new Error(check.message);
      return resolveOfficialSizeTemplateIds({
        shopId,
        catId: categoryId,
        required,
        mode,
        sizeClass,
        selectedBusinessIds,
        chartName: DEFAULT_CHART_NAME,
        records,
        groupList,
        elementList,
        isDoubleSize,
        api: temuPlatformAPI,
      });
    },
  }), [
    required, mode, sizeClass, selectedBusinessIds, records,
    groupList, elementList, shopId, categoryId, isDoubleSize,
  ]);

  const applyPlatformTemplate = (row) => {
    const businessId = Number(row.businessId || row.id || 0);
    if (suitMulti) {
      setSelectedBusinessIds((prev) => {
        if (prev.includes(businessId)) return prev;
        if (prev.length >= needCount) {
          return [...prev.slice(1), businessId];
        }
        return [...prev, businessId];
      });
      setMode('template');
      showToast(chartText('sizeChartPanel.pickedChart', `已选择尺码表「${row.name || businessId}」`, { name: row.name || businessId }), 'success');
      return;
    }

    if (!normalizedSizeLabels.length) {
      showToast(chartText('sizeChartPanel.pickSizeBeforeLib', '请先在规格供应中选择尺码，再从尺码表库带出尺寸'), 'warning');
      setSelectedLibraryId('');
      return;
    }

    const applied = applyOfficialSizeTemplate(row, { groupList, elementList });
    if (applied.records?.length) {
      setRecords(syncRowsToSizeLabels(
        normalizedSizeLabels,
        groupList,
        elementList,
        applied.records,
      ));
      setSelectedBusinessIds([]);
      setMode('create');
      showToast(
        applied.filled
          ? chartText('sizeChartPanel.filledFromLib', `已从尺码表库带出尺寸（${applied.filled} 项），可继续修改`, { count: applied.filled })
          : chartText('sizeChartPanel.pickedCheck', '已选用尺码表库，请核对尺寸'),
        'success',
      );
      return;
    }
    if (businessId > 0) {
      setSelectedBusinessIds([businessId]);
      setMode('template');
      showToast(chartText('sizeChartPanel.pickedReuse', `已选用「${row.name || businessId}」（将按已有模板发品）`, { name: row.name || businessId }), 'success');
      return;
    }
    showToast(chartText('sizeChartPanel.noSizeData', '该尺码表无可用尺寸数据'), 'warning');
  };

  const applyIpeTemplate = useCallback(async (templateId, source, { auto = false } = {}) => {
    const id = String(templateId || '').trim();
    const lib = source === LIB_OFFICIAL ? LIB_OFFICIAL : LIB_IPE;
    if (!id) return;
    if (lib === LIB_IPE && !shopId) return;
    if (lib === LIB_OFFICIAL && !officialMode && !shopId) return;
    const nextValue = librarySelectValue(lib, id);
    if (!normalizedSizeLabels.length) {
      if (auto) {
        setSelectedLibraryId(nextValue);
        return;
      }
      showToast(chartText('sizeChartPanel.pickSizeBeforeIpe', '请先在规格供应中选择尺码，再从尺码库带出尺寸'), 'warning');
      setSelectedLibraryId('');
      return;
    }
    try {
      const res = await fetchSizeChartLibraryDetail({
        officialMode,
        shopId,
        source: lib,
        id,
      });
      const item = res?.item || res || {};
      const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
      if (!isOfficialSizeChartTemplatePayload(payload)) {
        if (!auto) showToast(chartText('sizeChartPanel.badFormat', '该尺码库模板不是官方尺码表格式，请重新保存后再用'), 'warning');
        return;
      }
      const seeded = extractOfficialSizeRecords(payload);
      if (!seeded.length) {
        if (!auto) showToast(chartText('sizeChartPanel.noTemplateData', '该尺码库模板没有尺寸数据'), 'warning');
        return;
      }
      setRecords(syncRowsToSizeLabels(normalizedSizeLabels, groupList, elementList, seeded));
      const platformIds = (Array.isArray(payload.platformBusinessIds) ? payload.platformBusinessIds : [])
        .map((pid) => Number(pid))
        .filter((pid) => pid > 0);
      setSelectedBusinessIds(platformIds);
      setMode(platformIds.length ? 'template' : 'create');
      setSelectedLibraryId(nextValue);
      showToast(
        auto
          ? chartText('sizeChartPanel.autoFilled', `已按尺码库「${item.name || id}」自动填充尺寸`, { name: item.name || id })
          : chartText('sizeChartPanel.fromLib', `已从尺码库带出「${item.name || id}」`, { name: item.name || id }),
        'success',
      );
    } catch (e) {
      if (!auto) {
        showToast(e?.message || chartText('sizeChartPanel.loadTemplateFail', '加载尺码库模板失败'), 'error');
        setSelectedLibraryId('');
      }
    }
  }, [
    officialMode,
    shopId,
    normalizedSizeLabels,
    groupList,
    elementList,
    syncRowsToSizeLabels,
  ]);

  const applyLibraryTemplate = (rawValue) => {
    const value = String(rawValue || '').trim();
    setSelectedLibraryId(value);
    if (!value) {
      setSelectedBusinessIds([]);
      if (!suitMulti) {
        setRecords(syncRowsToSizeLabels(normalizedSizeLabels, groupList, elementList, []));
      }
      return;
    }
    const parsed = parseLibrarySelectValue(value);
    if (parsed.id) {
      void applyIpeTemplate(parsed.id, parsed.source);
      return;
    }
    const row = platformTemplates.find((t) => String(t.businessId || t.id) === value);
    if (!row) {
      showToast(chartText('sizeChartPanel.chartMissing', '未找到所选尺码表'), 'error');
      return;
    }
    applyPlatformTemplate(row);
  };

  useEffect(() => {
    autoAppliedKeyRef.current = '';
  }, [libraryTypeId, officialMode, shopId, shopType, sizeClass?.classId]);

  useEffect(() => {
    void reloadIpeTemplates();
  }, [reloadIpeTemplates]);

  useEffect(() => {
    if (hideLibraryActions || busy || loading || mode === 'skip') return undefined;
    if (!libraryTypeId || !ipeTemplates.length || !groupList.length) return undefined;
    if (officialSizeChartRecordsHaveValues(recordsRef.current, groupList)) return undefined;
    const picked = pickAutoRuleTemplate(ipeTemplates, { styleNo: defaultStyleNo });
    if (!picked?.id) return undefined;
    const source = picked._lib === LIB_OFFICIAL ? LIB_OFFICIAL : LIB_IPE;
    const nextValue = librarySelectValue(source, picked.id);
    const current = String(selectedLibraryId || '').trim();
    if (current && current !== nextValue) return undefined;
    const applyKey = `${nextValue}|${libraryTypeId}|${normalizedSizeLabels.join('\0')}`;
    if (autoAppliedKeyRef.current === applyKey) return undefined;
    autoAppliedKeyRef.current = applyKey;
    void applyIpeTemplate(String(picked.id), source, { auto: true });
    return undefined;
  }, [
    hideLibraryActions,
    busy,
    loading,
    mode,
    libraryTypeId,
    ipeTemplates,
    groupList,
    defaultStyleNo,
    selectedLibraryId,
    normalizedSizeLabels,
    applyIpeTemplate,
  ]);

  const openSaveTemplateModal = () => {
    if (suitMulti) {
      showToast(chartText('sizeChartPanel.suitUseLib', '套装类目请从尺码表库选用已有模板'), 'warning');
      return;
    }
    if (!normalizedSizeLabels.length) {
      showToast(chartText('sizeChartPanel.fillSizesFirst', '请先在规格供应中选择尺码并填写尺寸'), 'warning');
      return;
    }
    if (!productTypeId) {
      showToast(chartText('sizeChartPanel.missingProductType', '缺少商品类型，请重新选择类目后再保存'), 'warning');
      return;
    }
    const check = validateOfficialSizeChartDraft({
      required: true,
      mode: 'create',
      sizeClass,
      records,
      groupList,
      elementList,
    });
    if (!check.ok) {
      showToast(check.message || chartText('sizeChartPanel.fillComplete', '请先完整填写尺寸信息'), 'error');
      return;
    }
    setSaveModalOpen(true);
  };

  const officialDraftForSave = useMemo(() => ({
    mode: 'create',
    sizeClass,
    selectedBusinessIds: [],
    selectedLibraryId: '',
    records,
    groupList,
    elementList,
    isDoubleSize: shoeClass ? Boolean(isDoubleSize) : false,
  }), [sizeClass, records, groupList, elementList, shoeClass, isDoubleSize]);

  // 鞋类：拉 foot.get（失败用文档快照），并按单/双码回填测量列
  useEffect(() => {
    if (!shoeClass) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = officialMode
          ? await adminAPI.getOfficialTemuCatalogSizeChartFoot({
            shopType,
            shoeSizeType: 0,
          })
          : (shopId
            ? await temuPlatformAPI.getPublishSizeChartFoot(shopId, { shoeSizeType: 0 })
            : getStaticShoeFootMap());
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        if (!cancelled && payload && typeof payload === 'object') {
          setShoeFootMap({ ...getStaticShoeFootMap(), ...payload });
        }
      } catch {
        if (!cancelled) setShoeFootMap(getStaticShoeFootMap());
      }
    };
    load();
    return () => { cancelled = true; };
  }, [shoeClass, officialMode, shopType, shopId]);

  useEffect(() => {
    if (!shoeClass || busy || loading || mode === 'skip') return;
    if (!normalizedSizeLabels.length || !groupList.length) return;
    setRecords((prev) => syncRowsToSizeLabels(
      normalizedSizeLabels,
      groupList,
      elementList,
      prev,
    ));
  }, [
    shoeClass,
    isDoubleSize,
    shoeFootMap,
    normalizedSizeLabels,
    groupList,
    elementList,
    busy,
    loading,
    mode,
    syncRowsToSizeLabels,
  ]);

  const draftOfflineReady = hideLibraryActions
    && Array.isArray(records)
    && records.length > 0;

  if (!categoryId && !draftOfflineReady) {
    return (
      <div className="tpo-size-chart tpo-official-size-chart" data-field-error="sizeChart">
        <div className="tpo-size-chart__head">
          <div className="tpo-size-chart__title-row">
            <h4 className="tpo-publish__subhead">{chartText('sizeChartPanel.sectionTitle', fixedUi("尺寸信息"))}</h4>
          </div>
        </div>
        <p className="tpo-wizard__inline-hint">{chartText('sizeChartPanel.pickCategory', fixedUi("请先选择类目"))}</p>
      </div>
    );
  }

  if (categoryId && !required && !draftOfflineReady) {
    return (
      <div className="tpo-size-chart tpo-official-size-chart" data-field-error="sizeChart">
        <div className="tpo-size-chart__head">
          <div className="tpo-size-chart__title-row">
            <h4 className="tpo-publish__subhead">{chartText('sizeChartPanel.sectionTitle', fixedUi("尺寸信息"))}</h4>
          </div>
        </div>
        <p className="tpo-wizard__inline-hint">{chartText('sizeChartPanel.notNeeded', fixedUi("该类目无需填写尺码表。"))}</p>
      </div>
    );
  }

  return (
    <div className="tpo-size-chart tpo-official-size-chart" data-field-error="sizeChart">
      <div className="tpo-size-chart__head">
        <div className="tpo-size-chart__title-row">
          <h4 className="tpo-publish__subhead">
            {chartText('sizeChartPanel.sectionTitle', '尺寸信息')} <RequiredMark />
          </h4>
        </div>
        {errorMessage ? <div className="tpo-field__error" role="alert">{errorMessage}</div> : null}
        {loadError ? <div className="tpo-field__error" role="alert">{loadError}</div> : null}
      </div>

      {!hideLibraryActions ? (
        <div className="tpo-size-chart-template-bar">
          <select
            className="tpo-select"
            value={selectedLibraryId}
            disabled={busy}
            onChange={(e) => applyLibraryTemplate(e.target.value)}
            aria-label={chartText('sizeChartPanel.pickChartLib', '选择尺码表库')}
          >
            <option value="">{chartText('sizeChartPanel.fromLibOpt', fixedUi("从尺码库选用（可选）"))}</option>
            {ipeTemplates.some((t) => t._lib === LIB_OFFICIAL) ? (
              <optgroup label={chartText('sizeChartPanel.groupOfficial', '官方尺码库')}>
                {ipeTemplates.filter((t) => t._lib === LIB_OFFICIAL).map((t) => {
                  const id = String(t.id || '');
                  const styleNo = String(t.styleNo || '').trim();
                  return (
                    <option key={`official-${id}`} value={librarySelectValue(LIB_OFFICIAL, id)}>
                      {styleNo ? `${styleNo} · ` : ''}{t.name || chartText('sizeChartPanel.chartNo', `尺码表#${id}`, { id })}
                    </option>
                  );
                })}
              </optgroup>
            ) : null}
            {ipeTemplates.some((t) => t._lib !== LIB_OFFICIAL) ? (
              <optgroup label={chartText('sizeChartPanel.groupIpe', '站内尺码库')}>
                {ipeTemplates.filter((t) => t._lib !== LIB_OFFICIAL).map((t) => {
                  const id = String(t.id || '');
                  const styleNo = String(t.styleNo || '').trim();
                  return (
                    <option key={`ipe-${id}`} value={librarySelectValue(LIB_IPE, id)}>
                      {styleNo ? `${styleNo} · ` : ''}{t.name || chartText('sizeChartPanel.chartNo', `尺码表#${id}`, { id })}
                    </option>
                  );
                })}
              </optgroup>
            ) : null}
            {platformTemplates.length ? (
              <optgroup label={chartText('sizeChartPanel.groupPlatform', '平台尺码表库')}>
                {platformTemplates.map((t) => {
                  const id = Number(t.businessId || t.id);
                  return (
                    <option key={`plat-${id}`} value={String(id)}>
                      {t.name || chartText('sizeChartPanel.chartNo', `尺码表#${id}`, { id })}
                    </option>
                  );
                })}
              </optgroup>
            ) : null}
          </select>
          {!suitMulti ? (
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={busy || !normalizedSizeLabels.length}
              onClick={openSaveTemplateModal}
            >
              {chartText('sizeChartPanel.saveAsTemplate', '保存为模板')}
            </button>
          ) : null}
          {suitMulti ? (
            <span className="tpo-wizard__inline-hint">
              {chartText('sizeChartPanel.suitNeed', `套装类目请选择 ${needCount} 个尺码表（已选 ${selectedBusinessIds.length}/${needCount}）`, { need: needCount, picked: selectedBusinessIds.length })}
            </span>
          ) : null}
        </div>
      ) : null}

      {busy ? <p className="tpo-wizard__inline-hint">{chartText('sizeChartPanel.loadingRules', fixedUi("加载尺码规则中…"))}</p> : null}

      {shoeClass && (!suitMulti || mode === 'create' || hideLibraryActions) ? (
        <div className="tpo-size-chart-shoe-mode" role="group" aria-label={chartText('sizeChartPanel.shoeAria', '鞋类单双码')}>
          <span className="tpo-field__label">{chartText('sizeChartPanel.shoeType', fixedUi("鞋码类型"))}</span>
          <label className="tpo-radio">
            <input
              type="radio"
              name={`shoe-double-${shopId || shopType || 'x'}`}
              checked={!isDoubleSize}
              onChange={() => setIsDoubleSize(false)}
              disabled={busy || loading}
            />
            {chartText('sizeChartPanel.shoeSingle', '单码')}
          </label>
          <label className="tpo-radio">
            <input
              type="radio"
              name={`shoe-double-${shopId || shopType || 'x'}`}
              checked={isDoubleSize}
              onChange={() => setIsDoubleSize(true)}
              disabled={busy || loading}
            />
            {chartText('sizeChartPanel.shoeDouble', '双码')}
          </label>
          <span className="tpo-wizard__inline-hint">
            {chartText('sizeChartPanel.shoeMap', '按平台脚长映射自动填充欧/英/美码与脚长；创建时传 isDoubleSize=')}
            {isDoubleSize ? 'true' : 'false'}
          </span>
        </div>
      ) : null}

      {suitMulti && mode === 'template' && !hideLibraryActions ? (
        <ul className="tpo-official-size-chart__tpl-list">
          {selectedBusinessIds.map((id) => {
            const t = platformTemplates.find((row) => Number(row.businessId || row.id) === id);
            return (
              <li key={id}>
                <button
                  type="button"
                  className="tpo-official-size-chart__tpl is-on"
                  onClick={() => setSelectedBusinessIds((prev) => prev.filter((x) => x !== id))}
                  title={chartText('sizeChartPanel.unselect', '点击取消选择')}
                >
                  {t?.name || chartText('sizeChartPanel.chartNo', `尺码表#${id}`, { id })} · {id}
                </button>
              </li>
            );
          })}
          {!selectedBusinessIds.length ? (
            <li className="tpo-wizard__inline-hint">{chartText('sizeChartPanel.pickFromLibAbove', fixedUi("请从上方尺码表库选择"))}</li>
          ) : null}
        </ul>
      ) : null}

      {!suitMulti || mode === 'create' || hideLibraryActions ? (
        normalizedSizeLabels.length === 0 ? (
          <p className="tpo-wizard__inline-hint">{emptySizeHint === '请先在规格供应中选择尺码，尺寸表将按所选尺码自动增减行。'
            ? chartText('sizeChartPanel.emptySize', '请先在规格供应中选择尺码，尺寸表将按所选尺码自动增减行。')
            : emptySizeHint}</p>
        ) : (
          <div className="tpo-publish__table-wrap">
            <table className="tpo-publish__table tpo-size-chart-table">
              <thead>
                <tr>
                  <th className="tpo-size-chart-table__size">{groupList[0]?.name || chartText('sizeChartPanel.sizeCol', fixedUi("尺码"))}</th>
                  {elementList.map((el) => (
                    <th key={el.id}>
                      {el.name}
                      {(el.necessary || !elementList.some((x) => x?.necessary)) ? <RequiredMark /> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((row, ri) => (
                  <tr key={`${row.values[groupId] || ri}`}>
                    <td className="tpo-size-chart-table__size">
                      {row.values[groupId] || '—'}
                    </td>
                    {elementList.map((el) => (
                      <td key={el.id}>
                        <input
                          className="tpo-input"
                          value={row.values[String(el.id)] || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            const id = String(el.id);
                            setRecords((prev) => prev.map((r, i) => (
                              i === ri ? { values: { ...r.values, [id]: v } } : r
                            )));
                            if (mode === 'template') setMode('create');
                            setSelectedBusinessIds([]);
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      <SaveSizeChartTemplateMetaModal
        open={saveModalOpen}
        shopId={shopId}
        officialMode={officialMode}
        shopType={shopType}
        categoryId={categoryId}
        productTypeId={productTypeId}
        categoryPathLabel={categoryPathLabel}
        selectedSizeIds={selectedSizeIds}
        selectedSizeLabels={normalizedSizeLabels}
        sizeClass={sizeClass}
        officialSizeChart={officialDraftForSave}
        defaultStyleNo={defaultStyleNo}
        onClose={() => setSaveModalOpen(false)}
        onSaved={async () => {
          await reloadIpeTemplates();
          if (officialMode || !shopId) return;
          try {
            const templatesRes = await temuPlatformAPI.listPublishSizeChartTemplates(shopId, {
              catId: Number(categoryId),
              offset: 0,
              pageSize: 50,
            });
            setPlatformTemplates(asSizeChartList(templatesRes));
          } catch {
            /* ignore */
          }
        }}
      />
    </div>
  );
});

export default TemuOfficialSizeChartPanel;

/** 拉取 class.get，供父组件判定必填。官方模式走 admin catalog 借店，不依赖用户 shopId。 */
export async function fetchSizeChartClassRequired(shopId, categoryId, opts = {}) {
  const officialMode = Boolean(opts.officialMode);
  const shopType = String(opts.shopType || '').trim();
  try {
    const raw = officialMode
      ? await adminAPI.getOfficialTemuCatalogSizeChartClass({
        shopType,
        catId: Number(categoryId),
        categoryId: Number(categoryId),
      })
      : await temuPlatformAPI.getPublishSizeChartClass(shopId, Number(categoryId));
    const parsed = parseSizeChartClass(raw);
    return { ok: true, ...parsed };
  } catch (e) {
    return { ok: false, required: false, classId: null, message: e?.message || chartText('sizeChartPanel.classRulesFail', '尺码表类目规则加载失败') };
  }
}
